import React from 'react';
import { StudioSession, AccessTier } from '../../types';
import { ACCESS_TIERS, TIER_LABELS, TIER_BADGES } from '../../config/accessTiers';
import { setActiveGhostToken } from '../../lib/ghostSession';
import { X, User, ShieldCheck, ExternalLink, Zap, Check } from 'lucide-react';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  session: StudioSession;
  onUpdateSession: (newSession: StudioSession) => void;
}

export const AuthModal: React.FC<AuthModalProps> = ({
  isOpen,
  onClose,
  session,
  onUpdateSession,
}) => {
  if (!isOpen) return null;

  const handleSimulateTier = async (tier: AccessTier) => {
    let email = 'guest@aixenergy.io';
    let name = 'Guest Visitor';
    let slug = 'free';

    if (tier === 'admin') {
      email = 'brandon@aixenergy.io';
      name = 'Brandon Owens (Staff Admin)';
      slug = 'admin';
    } else if (tier === 'studio_pro') {
      email = 'portfolio.manager@infrastructure.fund';
      name = 'Marcus Vance (Studio Pro)';
      slug = 'aixenergy-studio';
    } else if (tier === 'map_seat') {
      email = 'vp.energy@hyperscale.cloud';
      name = 'Elena Rostova (Map Seat)';
      slug = 'aixenergy-executive';
    } else if (tier === 'journal') {
      email = 'analyst@cleanenergy.org';
      name = 'Devon Price (Journal)';
      slug = 'aixenergy-quarterly';
    } else if (tier === 'free_member') {
      email = 'member@energytech.io';
      name = 'Jordan Hayes (Free Member)';
      slug = 'free';
    }

    const mockToken = `mock-token-${tier}-${Date.now()}`;
    setActiveGhostToken(mockToken);

    try {
      const res = await fetch('/api/auth/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          token: mockToken,
          member: {
            uuid: `ghost-${tier}-${Date.now()}`,
            email: email,
            name: name,
            tier: slug,
          }
        })
      });
      if (res.ok) {
        const data = await res.json();
        onUpdateSession({
          authenticated: tier !== 'guest',
          user_id: data.user_id,
          email: data.email,
          name: data.name,
          tier: data.tier,
        });
      }
    } catch {
      onUpdateSession({
        authenticated: tier !== 'guest',
        user_id: `mock-${tier}`,
        email: email,
        name: name,
        tier: tier,
      });
    }

    onClose();
  };

  const handleGhostPortalSignIn = () => {
    window.location.href = 'https://www.aixenergy.io/#/portal/signin';
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-150">
        {/* Modal Header */}
        <div className="p-4 border-b border-slate-800 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 rounded-lg bg-amber-500/20 border border-amber-500/40 flex items-center justify-center text-amber-400">
              <Zap className="w-4 h-4 fill-amber-400" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-white">Ghost Member SSO &amp; Tier Switcher</h3>
              <p className="text-[10px] text-slate-400 font-mono">docs/membership-gating.md Entitlements</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-4 space-y-4 text-xs">
          {/* Active Session Info */}
          <div className="p-3 rounded-lg bg-slate-950 border border-slate-800 flex items-center justify-between">
            <div>
              <span className="text-[10px] text-slate-500 font-mono block">CURRENT ACTIVE IDENTITY</span>
              <span className="font-bold text-white text-xs">{session.name}</span>
              <span className="text-[10px] text-slate-400 block">{session.email || 'Not signed in'}</span>
            </div>
            <span className={`px-2 py-0.5 rounded text-[10px] font-mono font-bold uppercase border ${TIER_BADGES[session.tier]?.bg} ${TIER_BADGES[session.tier]?.border} ${TIER_BADGES[session.tier]?.color}`}>
              {TIER_BADGES[session.tier]?.text || 'GUEST'}
            </span>
          </div>

          {/* Real Ghost SSO Button */}
          <button
            onClick={handleGhostPortalSignIn}
            className="w-full py-2 px-3 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 font-medium flex items-center justify-center space-x-2 transition-colors"
          >
            <span>Sign In via Ghost Portal (www.aixenergy.io)</span>
            <ExternalLink className="w-3.5 h-3.5 opacity-60" />
          </button>

          {/* Quick Simulation Tier Switcher */}
          <div>
            <span className="text-[10px] uppercase font-mono text-slate-400 font-bold block mb-2">
              Select Entitlement Tier to Test Gating &amp; Features:
            </span>
            <div className="space-y-1.5">
              {ACCESS_TIERS.map((tier) => {
                const isSelected = session.tier === tier;
                const badge = TIER_BADGES[tier];
                return (
                  <button
                    key={tier}
                    onClick={() => handleSimulateTier(tier)}
                    className={`w-full p-2.5 rounded-lg border text-left flex items-center justify-between transition-colors ${
                      isSelected
                        ? 'bg-amber-950/40 border-amber-500 text-white'
                        : 'bg-slate-950/60 border-slate-800 hover:bg-slate-800/60 text-slate-300'
                    }`}
                  >
                    <div>
                      <div className="font-bold text-xs flex items-center space-x-2">
                        <span>{TIER_LABELS[tier]}</span>
                        {isSelected && <Check className="w-3.5 h-3.5 text-amber-400" />}
                      </div>
                      <div className="text-[10px] text-slate-500 mt-0.5">
                        {tier === 'studio_pro' && 'Full National Comps, Buyout Ranges, Unlimited Scenarios, Clean PDF'}
                        {tier === 'admin' && 'Brandon Owens Staff Master Override & RLS Admin Access'}
                        {tier === 'map_seat' && 'Full National Comps, Full Radar, 10 Scenarios, Institutional PDF'}
                        {tier === 'journal' && 'Regional Comps, Top 15 Radar, 5 Scenarios, Standard Watermark'}
                        {tier === 'free_member' && 'Top 25 Comps, 5 Radar Assets, 1 Scenario, Sample Watermark'}
                        {tier === 'guest' && 'Top 10 Featured Comps, 3 Radar Previews, View-Only Sandbox'}
                      </div>
                    </div>
                    <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded border uppercase shrink-0 ${badge.bg} ${badge.border} ${badge.color}`}>
                      {badge.text}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
